import React, { useState, useEffect } from "react";
import _ from "lodash";
import {
  _getCSRFTokenValue,
  _findSelectOption,
  _validateForm,
} from "../../utils/helper";
import api from "../../utils/api";
// import StateView from "../../utils/StateView";

import ReactDOM from "react-dom"; // needed for ReactDOMServer to function
import ReactDOMServer from "react-dom/server";

import { Loader } from "../../atoms";
import { Tabs } from "../../molecules";
import { CheckboxTree, InputSelect, SingleCheckbox } from "../../forms";

const filterTypes = {
  quotaType: {
    label: "By quota type",
    options: [
      {
        name: "Legislated Candidate Quotas",
        value: "Legislated Candidate Quotas",
      },
      {
        name: "Reserved Seats",
        value: "Reserved Seats",
      },
      {
        name: "Political Party Quotas",
        value: "Political Party Quotas",
      },
    ],
  },
  // quotaSource: {
  //   label: "By quota source",
  //   options: [
  //     {
  //       name: "Constitution",
  //       value: "Constitution",
  //     },
  //     {
  //       name: "Electoral Law",
  //       value: "Electoral Law",
  //     },
  //     {
  //       name: "Political Funding Legislation",
  //       value: "Political Funding Legislation",
  //     },
  //   ],
  // },
};

const QUOTA_SOURCES_QIDS = {
  Constitution: [
    9164, // Single/Lower House - Constitutional quota details
    9179, // Upper house - Constitutional quota details
    9174, // Sub-National Level - Constitutional quota details
  ],
  "Electoral Law": [
    9163, // Single/Lower House - Electoral law quota details
    9170, // Upper house - Electoral law quota details
    9180, // Sub-National Level - Electoral law quota details
  ],
  "Political Funding Legislation": [
    9252, // Single/Lower House - Political funding legislation quota details
    9253, // Upper house - Political funding legislation quota details
    9254, // Sub-National Level - Political funding legislation quota details
  ],
};

const PRESELECT_QUESTIONS_BY_THEME = {
  // Voter Turnout
  293: [
    9188, // Parliamentary - Voter Turnout
    9113, // Parliamentary - Registration
    9115, // Parliamentary - Voting age population
    9121, // Parliamentary - Population
  ],
  // Gender Guotas
  297: [
    9157, // General - Parliament type
    9181, // General - Voluntary political party quotas
    9162, // Single/Lower House - Quota type
    9164, // Single/Lower House - Constitutional quota details
    9163, // Single/Lower House - Electoral law quota details
  ],
};

const startYear = 1945;
const currentYear = new Date().getFullYear();
const yearsOptions = [];

for (let year = startYear; year <= currentYear; year++) {
  const yearString = year.toString();
  yearsOptions.push({ value: yearString, label: yearString });
}

const historicalDatabasesIds = [
  293, // Voter Turnout Database
  307, // Electoral System Design Database
  326, // Voting from Abroad Database
];
const defaultInitialValue = "all";

const INITIAL_VALUES_ALL = {
  questionId: "",
  qid: [],
  countryId: "",
  cid: [],
  startDate: "",
  endDate: "",
  latestElection: false,
  dateRangeFieldsDisabled: true,
  quotaType: "",
  quotaSource: "",
};

const AdvancedSearch = (props) => {
  const [questionOptions, setQuestionOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [politicalEntityOptions, setPoliticalEntityOptions] = useState([]);
  const [values, setValues] = useState(INITIAL_VALUES_ALL);
  const [initialValue, setInitialValue] = useState(null);
  const [searchResults, setSearchResults] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isGenderQuotas, setIsGenderQuotas] = useState(false);
  const [isVoterTurnout, setIsVoterTurnout] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const themeId = props?.data?.tid ?? urlParams.get("tid") ?? null;

    if (themeId) {
      const databaseThemeId = parseInt(themeId);
      setInitialValue(databaseThemeId);
      setIsGenderQuotas(databaseThemeId === 297);
      setIsVoterTurnout(databaseThemeId === 293);
    } else {
      setInitialValue(defaultInitialValue);
    }
  }, [props?.data?.tid, window.location.search]);

  useEffect(() => {
    if (initialValue === defaultInitialValue) {
      // detect if only voter turnout questions are selected when on general search mode
      const questionIds = values.qid;
      if (questionIds.length > 0) {
        const subtreeIds = getAllIdsInTreeByAncestorIds(questionOptions, [293]);
        const idsToCheck = [293, ...subtreeIds];
        const containsNotFromIdsToCheck = questionIds.some(
          (item) => !idsToCheck.includes(item.id)
        );
        if (containsNotFromIdsToCheck) {
          setIsVoterTurnout(false);
        } else {
          setIsVoterTurnout(true);
        }
      } else {
        setIsVoterTurnout(false);
      }
    }
  }, [values.qid, questionOptions, initialValue]);

  useEffect(() => {
    // check if got value before fetching
    // in order to avoid multiple request when state change
    if (initialValue) {
      fetchFilterOptions();

      if (historicalDatabasesIds.includes(initialValue)) {
        setValues({ ...values, dateRangeFieldsDisabled: false });
      }
    }
  }, [initialValue]);

  useEffect(() => {
    if (
      Object.keys(searchResults).length > 0 &&
      typeof window.equaliseTableCellsWidth === "function"
    ) {
      // Call the function in the global scope
      window.equaliseTableCellsWidth();
    }
  }, [window, searchResults]);

  const fetchFilterOptions = async () => {
    setIsLoadingFilters(true);

    const csrfToken = await _getCSRFTokenValue();
    api
      .get("/advance-database", {
        params: {
          // Initial loads all the filter options
          initial: initialValue,
        },
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        setIsLoadingFilters(false);
        const questionsOptionsList = res.data.Questions;

        if (PRESELECT_QUESTIONS_BY_THEME.hasOwnProperty(initialValue)) {
          const qids = PRESELECT_QUESTIONS_BY_THEME[initialValue];
          const validQids = getValidIds(qids, questionsOptionsList);
          const preselectedQids = validQids.join(",");
          const preselectedQuestions = validQids.map((qid) => ({
            id: qid,
            idType: "qid",
          }));

          setValues({
            ...values,
            questionId: preselectedQids,
            qid: preselectedQuestions,
          });
        }

        setQuestionOptions(questionsOptionsList);
        setRegionOptions(res.data.Countries.Region);
        setCountryOptions(res.data.Countries.Alphabetically);
        setPoliticalEntityOptions(res.data.Countries.PoliticalEntity);
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const getValidIds = (checkIds, tree, validIds = []) => {
    for (const item of tree) {
      const id = item.tid || item.qid || item.cid || item.value;
      if (checkIds.includes(id)) {
        validIds.push(id);
      }
      if (item.children) {
        getValidIds(checkIds, item.children, validIds);
      }
    }
    return validIds;
  };

  const handleCheckboxChange = (type, selected, itemValue = null) => {
    const mapTypeToValuesKey = {
      qid: "questionId",
      cid: "countryId",
      quotaTypeArray: "quotaType",
      quotaSourceArray: "quotaSource",
    };
    const formKey = mapTypeToValuesKey.hasOwnProperty(type)
      ? mapTypeToValuesKey[type]
      : null;

    let newValues = {
      ...values,
      [type]: selected,
    };

    if (type === "qid" && isGenderQuotas && itemValue !== null) {
      Object.keys(QUOTA_SOURCES_QIDS).map((type) => {
        const qidsToCheck = QUOTA_SOURCES_QIDS[type];

        if (qidsToCheck.includes(itemValue.id)) {
          // check if selected ids contains qids from quota source questions
          const containsIds = selected.some((num) => qidsToCheck.includes(num));

          // if selected ids dont have source qids
          // reset quotaSource and quotaSourceArray
          if (!containsIds) {
            const currentQuotaSourceValues = newValues["quotaSourceArray"];

            if (
              Array.isArray(currentQuotaSourceValues) &&
              currentQuotaSourceValues.length > 0
            ) {
              const newQuotaSourceValues = currentQuotaSourceValues.filter(
                (obj) => obj.id !== type
              );

              newValues["quotaSourceArray"] = newQuotaSourceValues;
              newValues["quotaSource"] = convertArrayValuesToString(
                newQuotaSourceValues,
                "value"
              );
            }
          }
        }
      });
    }

    if (formKey) {
      const selectedIdsByTypeString = convertArrayValuesToString(
        selected,
        type
      );
      newValues = {
        ...newValues,
        [formKey]: selectedIdsByTypeString,
      };
    }

    setValues(newValues);
  };

  const convertArrayValuesToString = (selectedValues, type) => {
    const selectedIdsByTypeString = selectedValues
      .filter((item) => item.idType === type || item.idType === "value")
      .map((item) => item.id)
      .sort((a, b) => {
        if (type === "cid") {
          // Sort by ID when type is "cid"
          return String(a).localeCompare(String(b), undefined, {
            numeric: true,
          });
        } else {
          return 0; // Return 0 to maintain the original order for other types
        }
      })
      .join(","); // convert to string to make it ready for API

    return selectedIdsByTypeString;
  };

  useEffect(() => {
    if (!isGenderQuotas) {
      toggleTimeRangeFieldsDisabledState();
    }
  }, [isGenderQuotas, questionOptions, values.qid]);

  const toggleTimeRangeFieldsDisabledState = () => {
    // only returns the children ids
    const subtreeIds = getAllIdsInTreeByAncestorIds(
      questionOptions,
      historicalDatabasesIds
    );
    const targetIds = [...historicalDatabasesIds, ...subtreeIds];
    const selectedIds = values.qid;

    // if got ids other than historical subtree ids, disable the fields
    const containsNonTargetIds = selectedIds.some(
      (item) => !targetIds.includes(item.id)
    );

    // if empty, disable it
    const isDisabled = selectedIds.length > 0 ? containsNonTargetIds : true;

    let newValues = {
      ...values,
      dateRangeFieldsDisabled: isDisabled,
    };
    // reset other values
    if (isDisabled) {
      newValues.startDate = "";
      newValues.endDate = "";
      newValues.latestElection = false;
    }

    setValues(newValues);
  };

  const getAllIdsInTreeByAncestorIds = (tree, ancestorIds) => {
    const ids = [];

    const traverseTree = (items) => {
      for (const item of items) {
        ids.push(item.tid || item.qid); // Collect the current item's ID

        if (item.children) {
          traverseTree(item.children); // Recursively traverse the children
        }
      }
    };

    for (const ancestorId of ancestorIds) {
      const ancestorItem = tree.find(
        (item) => item.tid === ancestorId || item.qid === ancestorId
      );
      if (ancestorItem) {
        traverseTree(ancestorItem.children || []);
      }
    }

    return ids;
  };

  const handleFilterTypesDisabledState = (type, selectedQuestions) => {
    const qids = selectedQuestions.map((obj) => obj.id);

    if (type === "quotaType") {
      const idsToCheck = [
        9162, // Single/Lower House - Quota type
        9169, // Upper house - Quota type
        9173, // Sub-National Level - Quota type
      ];
      const containsIds = qids.some((num) => idsToCheck.includes(num));
      return containsIds ? false : true;
    }

    if (type === "quotaSource") {
      const disabledOptions = [];
      Object.keys(QUOTA_SOURCES_QIDS).map((type) => {
        const qidsToCheck = QUOTA_SOURCES_QIDS[type];
        const containsIds = qids.some((num) => qidsToCheck.includes(num));
        if (!containsIds) {
          disabledOptions.push(type);
        }
      });

      return disabledOptions.length > 0 ? disabledOptions : false;
    }

    return false;
  };

  const getSearchResults = async () => {
    setErrors({});
    setSearchResults([]);

    const searchKeys = {
      ...values,
      isVoterTurnout,
    };

    // remove irrelevant keys
    delete searchKeys.qid;
    delete searchKeys.cid;
    delete searchKeys.quotaTypeArray;
    delete searchKeys.quotaSourceArray;

    const checkKeys = {
      questionId: "Please select at least 1 question/database",
      countryId: "Please select at least 1 country",
    };
    const validate = _validateForm(checkKeys, searchKeys);
    if (Object.keys(validate).length > 0) {
      setErrors(validate);
      return;
    }

    setIsLoadingResults(true);
    const csrfToken = await _getCSRFTokenValue();
    api
      .get("/advance-database", {
        params: searchKeys,
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        setIsLoadingResults(false);
        setSearchResults({
          themes: res.data.QuestionsGroupedByThemes,
          countries: res.data.ResultsCountries,
          countriesTaxonomies: res.data.CountriesTaxonomies,
          hasHistoricalData: !searchKeys.dateRangeFieldsDisabled,
          summary: res.data.SummaryData,
        });

        console.log('res.data.ResultsCountries', res.data.ResultsCountries);
      })
      .catch(() => {
        setIsLoadingResults(false);
      });

    // const sendChunkedRequests = async () => {
    //   setIsLoadingResults(true);
    //   const countryIdsArray = searchKeys.countryId.split(",");
    //   let accumulatedResults = {
    //     themes: [],
    //     countries: {},
    //     summary: {},
    //   };

    //   while (countryIdsArray.length > 0) {
    //     const chunk = countryIdsArray.splice(0, 3);
    //     const newSearchKeys = {
    //       ...searchKeys,
    //       countryId: chunk.join(","),
    //     };
    //     const resData = await sendRequestToAPI(newSearchKeys);
    //     console.log(
    //       "%c accumulatedResults ",
    //       "background: #000; color: #fff",
    //       accumulatedResults
    //     );
    //     accumulatedResults = {
    //       ...accumulatedResults,
    //       themes: resData?.QuestionsGroupedByThemes,
    //       countries: {
    //         ...accumulatedResults?.countries,
    //         ...resData?.ResultsCountries,
    //       },
    //       summary: { ...accumulatedResults?.summary, ...resData?.SummaryData },
    //       hasHistoricalData: !newSearchKeys.dateRangeFieldsDisabled,
    //     };
    //     console.log(
    //       "%c resData ",
    //       "background: #4299E1; color: #fff",
    //       resData,
    //       accumulatedResults
    //     );
    //     setSearchResults(accumulatedResults);
    //   }

    //   setIsLoadingResults(false);
    // };

    // sendChunkedRequests();
  };

  // const sendRequestToAPI = async (searchKeys) => {
  //   const csrfToken = await _getCSRFTokenValue();
  //   try {
  //     const response = await api.get("/advance-database", {
  //       params: searchKeys,
  //       headers: {
  //         "X-CSRF-Token": csrfToken,
  //       },
  //     });
  //     return response.data; // Return the response data
  //   } catch (error) {
  //     setIsLoadingResults(false);
  //     // Handle error if needed
  //     return null;
  //   }
  // };

  const SubthemeAndQuestionTableHeading = ({ themes, ...moreProps }) => {
    return themes.map((theme) =>
      theme.questions.map((question) => (
        <th className="align-bottom">
          {question.subthemePaths.map((subthemeName) => (
            <div className="font-bold">
              {subthemeName}
              {moreProps?.export ? " > " : null}
            </div>
          ))}
          <div className="!font-normal">{question.title}</div>
        </th>
      ))
    );
  };

  const replaceHyphen = (s) => {
    return s.replace(/-/g, "&#8209;");
  }

  const countTotalQuestion = (themes) => {
    let count = 0;
    
    themes.forEach((item) => {
      count += item.questions.length;
    });

    return count;
  }

  const ResultsTable = ({
    themes,
    countries,
    countriesTaxonomies,
    hasHistoricalData,
    ...tableProps
  }) => {
    const themeQuestionCount = {};

    themes.forEach((item) => {
      item.questions.sort((a, b) => {
        return a.weight - b.weight;
      });

      const theme = item.theme;
      const questionCount = item.questions.length;
      themeQuestionCount[theme] = questionCount;
    });

    return (
      <table className={`database-results ${countTotalQuestion(themes) > 8 ? 'table-width compact' : ''}`}>
        <thead>
          <tr>
            <th className="align-bottom unsticky"></th>
            <th className="align-bottom unsticky hidden"></th>
            <th className="align-bottom unsticky hidden"></th>
            {hasHistoricalData && <th className="align-bottom"></th>}
            {themes.map((theme) => (
              <th
                className="align-bottom"
                colSpan={themeQuestionCount[theme.theme]}
              >
                {theme.theme}
              </th>
            ))}
          </tr>
          <tr className="tw-ref-row">
            <th className="align-bottom">Country</th>
            <th className="align-bottom z-10 hidden">ISO2</th>
            <th className="align-bottom z-10 hidden">ISO3</th>
            {hasHistoricalData && (
              <th className="align-bottom">
                {isVoterTurnout ? "Date" : "Year"}
              </th>
            )}
            <SubthemeAndQuestionTableHeading
              themes={themes}
              export={tableProps?.export}
            />
          </tr>
        </thead>
        <tbody>
          {Object.keys(countries).map((country) => {
            const answerRows = countries[country];
            return answerRows.length < 1 ? false : (
              answerRows.map((row, rowIndex) => {
                const answersData = row.data;
                const countryId = getCountryIdByName(country);

                return (
                  <tr>
                    {rowIndex < 1 && (
                      <th rowSpan={answerRows.length}>
                        {countryId && isGenderQuotas && !tableProps?.export ? (
                          <a
                            href={`/data-tools/data/gender-quotas-database/country?country=${countryId}`}
                            className="tema-text--link"
                            target="_blank"
                          >
                            {country}
                          </a>
                        ) : (
                          country
                        )}
                      </th>
                    )}

                    {rowIndex < 1 && (
                      <th rowSpan={answerRows.length} className="hidden">{countriesTaxonomies[countryId]['iso2']}</th>
                    )}

                    {rowIndex < 1 && (
                      <th rowSpan={answerRows.length} className="hidden">{countriesTaxonomies[countryId]['iso3']}</th>
                    )}

                    {hasHistoricalData && <td dangerouslySetInnerHTML={{__html: row.year ? replaceHyphen(row.year) : "-"}}></td>}
                    {themes.map((theme) =>
                      theme.questions.map((q) => {
                        // Cross check with questions list to map to correct cells number
                        const answerExists = answersData[q.qid];
                        return answerExists && answerExists?.value ? (
                          <td>
                            {tableProps?.export === true ? (
                              answerExists.value
                            ) : (
                              <button
                                className="text-left tema-text--link answer-tooltip"
                                data-id={answerExists.nid}
                              >
                                {answerExists.value}
                              </button>
                            )}
                          </td>
                        ) : (
                          <td>-</td>
                        );
                      })
                    )}
                  </tr>
                );
              })
            );
          })}
        </tbody>
      </table>
    );
  };

  const ExportTable = ({
    themes,
    countries,
    countriesTaxonomies,
    hasHistoricalData,
    ...tableProps
  }) => {
    const themeQuestionCount = {};

    themes.forEach((item) => {
      item.questions.sort((a, b) => {
        return a.weight - b.weight;
      });
      
      const theme = item.theme;
      const questionCount = item.questions.length;
      themeQuestionCount[theme] = questionCount;
    });

    return (
      <table className={`database-results ${countTotalQuestion(themes) > 8 ? 'table-width compact' : ''}`}>
        <thead>
          <tr>
            <th className="align-bottom unsticky"></th>
            <th className="align-bottom unsticky hidden"></th>
            <th className="align-bottom unsticky hidden"></th>
            {hasHistoricalData && <th className="align-bottom"></th>}
            {themes.map((theme) => (
              <th
                className="align-bottom"
                colSpan={themeQuestionCount[theme.theme]}
              >
                {theme.theme}
              </th>
            ))}
          </tr>
          <tr className="tw-ref-row">
            <th className="align-bottom">Country</th>
            <th className="align-bottom z-10 hidden">ISO2</th>
            <th className="align-bottom z-10 hidden">ISO3</th>
            {hasHistoricalData && (
              <th className="align-bottom">
                {isVoterTurnout ? "Date" : "Year"}
              </th>
            )}
            <SubthemeAndQuestionTableHeading
              themes={themes}
              export={tableProps?.export}
            />
          </tr>
        </thead>
        <tbody>
          {Object.keys(countries).map((country) => {
            const answerRows = countries[country];
            return answerRows.length < 1 ? false : (
              answerRows.map((row, rowIndex) => {
                const answersData = row.data;
                const countryId = getCountryIdByName(country);

                return (
                  <tr>
                    <th>
                      {countryId && isGenderQuotas && !tableProps?.export ? (
                        <a
                          href={`/data-tools/data/gender-quotas-database/country?country=${countryId}`}
                          className="tema-text--link"
                          target="_blank"
                        >
                          {country}
                        </a>
                      ) : (
                        country
                      )}
                    </th>

                    <th className="hidden">{countriesTaxonomies[countryId]['iso2']}</th>

                    <th className="hidden">{countriesTaxonomies[countryId]['iso3']}</th>

                    {hasHistoricalData && <td dangerouslySetInnerHTML={{__html: row.year ? replaceHyphen(row.year) : "-"}}></td>}
                    {themes.map((theme) =>
                      theme.questions.map((q) => {
                        // Cross check with questions list to map to correct cells number
                        const answerExists = answersData[q.qid];
                        return answerExists && answerExists?.value ? (
                          <td>
                            {tableProps?.export === true ? (
                              answerExists.value
                            ) : (
                              <button
                                className="text-left tema-text--link answer-tooltip"
                                data-id={answerExists.nid}
                              >
                                {answerExists.value}
                              </button>
                            )}
                          </td>
                        ) : (
                          <td>-</td>
                        );
                      })
                    )}
                  </tr>
                );
              })
            );
          })}
        </tbody>
      </table>
    );
  };

  const getCountryIdByName = (countryName) => {
    let countryId = null;
    if (countryOptions.length > 0) {
      let foundCountry = countryOptions.find(
        (country) => country.name === countryName
      );
      if (foundCountry) {
        countryId = foundCountry.cid;
      }
    }
    return countryId;
  };

  const SummaryTable = ({ themes, summary }) => {
    const themeQuestionCount = {};
    themes.forEach((item) => {
      const theme = item.theme;
      const questionCount = Object.keys(item.questions).length;
      themeQuestionCount[theme] = questionCount;
    });

    return (
      <table className="database-results summary td-align-middle table-width">
        <thead>
          <tr>
            <th></th>
            {themes.map((theme) => (
              <th
                className="align-bottom"
                colSpan={themeQuestionCount[theme.theme]}
              >
                {theme.theme}
              </th>
            ))}
          </tr>
          <tr className="tw-ref-row">
            <th className="align-bottom">
              <h3 className="h3 !mb-0">Summary data</h3>
            </th>
            <SubthemeAndQuestionTableHeading themes={themes} />
          </tr>
        </thead>
        <tbody>
          <tr className="!bg-transparent">
            <td></td>
            {themes.map((theme) =>
              theme.questions.map((q) => {
                const item = summary[q.qid];
                const showNoData =
                  item.hasOwnProperty("choices") &&
                  Object.keys(item?.choices).includes("No data")
                    ? false
                    : true;

                return (
                  item && (
                    <td className="!align-top">
                      <table>
                        <tbody>
                          <tr>
                            <td className="font-bold">Value</td>
                            <td className="font-bold">Count</td>
                          </tr>
                          {item.hasOwnProperty("choices") &&
                            Object.keys(item?.choices).map((choiceKey) => (
                              <tr>
                                <td>
                                  <span class="text-ideaOrange100">
                                    {choiceKey}
                                  </span>
                                </td>
                                <td>{item.choices[choiceKey]}</td>
                              </tr>
                            ))}
                          {showNoData && item.hasOwnProperty("no_data") && (
                            <tr>
                              <td>
                                <span className="text-ideaOrange100">
                                  No data
                                </span>
                              </td>
                              <td>{item.no_data}</td>
                            </tr>
                          )}
                          {item.hasOwnProperty("other") && (
                            <tr>
                              <td>
                                <span className="text-ideaOrange100">
                                  Other
                                </span>
                              </td>
                              <td>{item.other}</td>
                            </tr>
                          )}
                          {item.hasOwnProperty("total") && (
                            <tr>
                              <td>
                                <span className="font-bold text-gray-700">
                                  Total
                                </span>
                              </td>
                              <td>{item.total}</td>
                            </tr>
                          )}
                          {item.hasOwnProperty("missing_data") && (
                            <tr>
                              <td>Missing data</td>
                              <td>{item.missing_data}</td>
                            </tr>
                          )}
                          {item.hasOwnProperty("grand_total") && (
                            <tr>
                              <td>
                                <span className="font-bold">Grand total</span>
                              </td>
                              <td>{item.grand_total}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </td>
                  )
                );
              })
            )}
          </tr>
        </tbody>
      </table>
    );
  };

  const getHTMLTableString = (type = "result") => {
    return ReactDOMServer.renderToString(
      type === "summary" ? (
        <SummaryTable
          themes={searchResults.themes}
          summary={searchResults.summary}
          export={true}
        />
      ) : (
        <ExportTable
          themes={searchResults.themes}
          countries={searchResults.countries}
          countriesTaxonomies={searchResults.countriesTaxonomies}
          hasHistoricalData={searchResults.hasHistoricalData}
          export={true}
        />
      )
    );
  };

  // const exportData = (type = "result") => {
  //   const tableString = getHTMLTableString(type);
  //   const apiURL = "/api/data-tools/export-advance?data=";
  //   window.open(apiURL + encodeURIComponent(tableString));
  // };

  return (
    <React.Fragment>
      <section className="mt-6 mb-8 db-questions-selectors lg:mb-12">
        <div className="mb-4 font-bold">Select database (and questions)</div>
        {isLoadingFilters ? (
          <Loader align="text-left" />
        ) : questionOptions.length > 0 ? (
          <CheckboxTree
            data={questionOptions}
            name="questionOptions"
            values={values.qid}
            onChange={(selected, itemValue) => {
              if (isGenderQuotas) {
                handleCheckboxChange("qid", selected, itemValue);
              } else {
                handleCheckboxChange("qid", selected);
              }
            }}
            error={_.get(errors, "questionId[0]")}
          />
        ) : null}
      </section>
      <section className="my-8 geographic-selectors lg:my-12">
        <div className="mb-4 font-bold">Select the geographic scope</div>
        <Tabs
          items={[
            {
              label: "By Region",
              content: isLoadingFilters ? (
                <Loader align="text-left" />
              ) : regionOptions.length > 0 ? (
                <CheckboxTree
                  data={regionOptions}
                  name="regionOptions"
                  values={values.cid}
                  onChange={(selected) => {
                    handleCheckboxChange("cid", selected);
                  }}
                  error={_.get(errors, "countryId[0]")}
                />
              ) : null,
            },
            {
              label: "Alphabetically",
              content: isLoadingFilters ? (
                <Loader align="text-left" />
              ) : countryOptions.length > 0 ? (
                <CheckboxTree
                  data={countryOptions}
                  name="countryOptions"
                  values={values.cid}
                  onChange={(selected) => {
                    handleCheckboxChange("cid", selected);
                  }}
                  actionButtonsType="selectAll"
                  error={_.get(errors, "countryId[0]")}
                  treeClass="columns-2 sm:columns-3 lg:columns-4 gap-x-8"
                />
              ) : null,
            },
            {
              label: "By Political entity",
              content: isLoadingFilters ? (
                <Loader align="text-left" />
              ) : politicalEntityOptions.length > 0 ? (
                <CheckboxTree
                  data={politicalEntityOptions}
                  name="politicalEntityOptions"
                  values={values.cid}
                  onChange={(selected) => {
                    handleCheckboxChange("cid", selected);
                  }}
                  error={_.get(errors, "countryId[0]")}
                />
              ) : null,
            },
          ]}
        />
      </section>
      {isGenderQuotas && (
        <section className="my-8 db-filters-selectors lg:my-12">
          <div className="font-bold">Filter the results</div>
          <div className="mb-6 text-mobileS md:text-desktopS">
            The following fields are optional, leaving them unselected will
            query all results.
          </div>
          {isLoadingFilters ? (
            <Loader align="text-left" />
          ) : Object.keys(filterTypes).length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-8">
              {Object.keys(filterTypes).map((type) => (
                <div>
                  <div className="font-bold text-mobileS md:text-desktopS">
                    {filterTypes[type].label}
                  </div>
                  <CheckboxTree
                    data={filterTypes[type].options}
                    name={`filterTypes${type}`}
                    values={
                      values.hasOwnProperty(`${type}Array`)
                        ? values[`${type}Array`]
                        : []
                    }
                    onChange={(selected) => {
                      handleCheckboxChange(`${type}Array`, selected);
                    }}
                    error={_.get(errors, `${type}Array[0]`)}
                    disableActionButtons
                    disabled={handleFilterTypesDisabledState(type, values.qid)}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </section>
      )}
      {(historicalDatabasesIds.includes(initialValue) ||
        initialValue === defaultInitialValue) && (
        <section className="my-8 time-range-selectors lg:my-12">
          <div className="font-bold">Time options</div>
          <p className="text-mobileS md:text-desktopS">
            Time options are available only for databases that contain
            historical data.
          </p>

          <div className="flex flex-wrap items-center gap-y-4 gap-x-8">
            <InputSelect
              label="From"
              options={yearsOptions}
              isClearable
              isSearchable
              isDisabled={values.dateRangeFieldsDisabled}
              value={_findSelectOption(yearsOptions, values.startDate)}
              onChange={(name, selectedValue) => {
                setValues({ ...values, startDate: selectedValue });
              }}
              class="min-w-[150px]"
            />
            <InputSelect
              label="To"
              options={yearsOptions}
              isClearable
              isSearchable
              isDisabled={values.dateRangeFieldsDisabled}
              value={_findSelectOption(yearsOptions, values.endDate)}
              onChange={(name, selectedValue) => {
                setValues({ ...values, endDate: selectedValue });
              }}
              class="min-w-[150px]"
            />
            <SingleCheckbox
              label="Most recent election"
              value={values.latestElection}
              onChange={(value) => {
                setValues({ ...values, latestElection: value });
              }}
              disabled={values.dateRangeFieldsDisabled}
              class="mt-auto mb-2"
            />
          </div>
        </section>
      )}

      {Object.keys(errors).length > 0 && (
        <div className="my-2 text-red-500 form-error text-desktopXS">
          There are items that require your attention. Please correct the errors
          above and try again.
        </div>
      )}

      <button className="btn btn-s--pill" onClick={() => getSearchResults()}>
        Search
      </button>

      {isLoadingResults && <Loader />}
      {Object.keys(searchResults).length > 0 ? (
        <React.Fragment>
          <hr className="my-8" />
          <div className="overflow-x-auto table-container">
            <ResultsTable
              themes={searchResults.themes}
              countries={searchResults.countries}
              countriesTaxonomies={searchResults.countriesTaxonomies}
              hasHistoricalData={searchResults.hasHistoricalData}
            />
          </div>

          <form
            id="results-table-form"
            action="/api/data-tools/export-advance"
            class="!hidden"
            target="_blank"
          >
            <textarea name="data" id="data">
              {getHTMLTableString()}
            </textarea>
          </form>

          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              type="submit"
              form="results-table-form"
              formMethod="post"
              className="btn btn-s--pill btn-c--hollow"
            >
              Export
            </button>
          </div>

          {searchResults?.summary && !searchResults.hasHistoricalData && (
            <React.Fragment>
              <div className="my-8">
                <h2 className="h2">Summary</h2>
                <div className="overflow-x-auto table-container">
                  <SummaryTable
                    themes={searchResults.themes}
                    summary={searchResults.summary}
                  />
                </div>
              </div>
            </React.Fragment>
          )}
        </React.Fragment>
      ) : null}

      {/* <StateView title="PPQ" values={searchResults.isPoliticalPartyQuotasSelected} class="top-4 bottom-[unset] left-4 right-[unset]" floating /> */}
    </React.Fragment>
  );
};
export default AdvancedSearch;
